import { Suspense } from "react";
import { useAtomValue } from "jotai";
import Link from "next/link";
import classNames from "classnames";
import { Icon } from "@zapier/design-system";
import { AssetType, isZapPublishedAsyncAtomFamily } from "@zapier/canvas-state";
import { getAssetLink } from "../../utils/getAssetLink";
import { getContrastingTextColor } from "../Canvas/nodeTypes/AssetNodeSkeleton/utils";
import { ConditionalTooltip } from "../ConditionalTooltip";
import styles from "./AssetLink.module.css";

type Props = {
  assetType: AssetType;
  assetId: string;
  assetName: string | undefined;
  interfaceProjectId: string | undefined;
  isViewOnlyMode: boolean;
  color: string;
};

function SuspendedComponent({
  assetType,
  assetId,
  assetName,
  interfaceProjectId,
  isViewOnlyMode,
  color,
}: Props) {
  const zapId = assetType === "zap" ? assetId : undefined;
  const isZapPublished = useAtomValue(isZapPublishedAsyncAtomFamily(zapId));

  const assetLink = getAssetLink({
    assetType,
    assetId,
    interfaceProjectId,
    isZapPublished,
  });

  if (!assetLink) {
    return null;
  }

  return (
    <ConditionalTooltip
      content={`Open ${assetName}`}
      isViewOnlyMode={isViewOnlyMode}
    >
      <div
        className={classNames({
          [styles.isViewOnlyModeWrapper]: isViewOnlyMode,
        })}
      >
        <Link
          href={assetLink}
          target="_blank"
          className={classNames(styles.offsiteLink, {
            [styles.isViewOnlyMode]: isViewOnlyMode,
          })}
          style={
            {
              color: getContrastingTextColor(color!, true),
            } as React.CSSProperties
          }
        >
          <Icon name="arrowOffsite" size={15} />
        </Link>
      </div>
    </ConditionalTooltip>
  );
}

export function AssetLink({
  assetType,
  assetId,
  assetName,
  interfaceProjectId,
  isViewOnlyMode,
  color,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        assetType={assetType}
        assetId={assetId}
        assetName={assetName}
        interfaceProjectId={interfaceProjectId}
        isViewOnlyMode={isViewOnlyMode}
        color={color}
      />
    </Suspense>
  );
}
